import React, { useEffect, useState } from "react";
import {
  User,
  X,
  Camera,
  Shield,
  Bell,
  Trash2,
  Plus,
  Sparkles,
  ChevronRight,
  MapPin,
  DollarSign,
  Clock,
  GraduationCap,
  BookOpen,
} from "lucide-react";
import { motion } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import { NavLink } from "react-router";
import SectionCard from "../../Components/Dashboard/ProfilePage/SectionCard";
import FieldRow from "../../Components/Dashboard/ProfilePage/FieldRow";
import countryData from "../../Data/countryCode.json";
import type { SchoolInfo, SpmGrade, Student } from "../../types/student";
import { computeProfileCompletion } from "../../utils/profileCompletion";
import {
  STATES,
  QUALIFICATIONS,
  GRADES,
  SUBJECTS,
  INTERESTS,
  BUDGETS,
} from "../../Data/studentData";

// ─── Profile completion calculation ─────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────

type FirestoreTimestampLike = {
  seconds?: number;
  toDate?: () => Date;
};

function normalizeSchoolInfo(user?: Partial<Student> | null): SchoolInfo {
  if (user?.school && typeof user.school === "object") {
    return {
      schoolName: user.school.schoolName || "",
      schoolState: user.school.schoolState || "",
      schoolYear: user.school.schoolYear || "",
    };
  }

  return {
    schoolName: typeof user?.school === "string" ? user.school : "",
    schoolState: user?.state || "",
    schoolYear: user?.spmYear ? String(user.spmYear) : "",
  };
}

function getUserQualification(user?: Partial<Student> | null): string {
  return (
    user?.qualification ||
    user?.qualifications ||
    user?.courseFinderAnswers?.qualification ||
    user?.courseFinderAnswers?.qualifications ||
    ""
  );
}

function getUserResultDetails(user?: Partial<Student> | null): SpmGrade[] {
  return user?.resultDetails || user?.spmGrades || [];
}

function isKnownQualification(qualification: string): boolean {
  return QUALIFICATIONS.some(
    (option) =>
      option.id === qualification || option.aliases?.includes(qualification),
  );
}

function getQualificationOption(qualification: string): string {
  if (!qualification) return "";
  return (
    QUALIFICATIONS.find(
      (option) =>
        option.id === qualification || option.aliases?.includes(qualification),
    )?.id || "Other"
  );
}

function getOtherQualification(qualification: string): string {
  if (!qualification || isKnownQualification(qualification)) return "";
  return qualification;
}

function getBudgetOption(budget: string) {
  return BUDGETS.find(
    (option) =>
      option.id === budget ||
      option.label === budget ||
      option.aliases?.includes(budget),
  );
}

function formatStudyPathway(pathway?: string): string {
  if (pathway === "Both") return "🌐 Local & Overseas";
  if (pathway === "Local") return "🇲🇾 Local only";
  if (pathway === "Overseas") return "🌍 Overseas only";
  return "";
}

const CGPA_QUALIFICATIONS = ["STPM", "Diploma", "Foundation", "Other"];
const OTHER_SUBJECT = "Others";
const OTHER_INTEREST = "Other";
const NOT_SURE_INTEREST = "Not sure";

function shouldUseCgpaInput(qualification: string): boolean {
  return CGPA_QUALIFICATIONS.includes(qualification);
}

function stripCgpaSuffix(value: string): string {
  return value.replace(/\s*CGPA\s*$/i, "").trim();
}

function getInterestOptionId(interest: string): string {
  return (
    INTERESTS.find(
      (option) => option.id === interest || option.label === interest,
    )?.id || ""
  );
}

function getKnownInterestIds(interests?: string[]): string[] {
  return Array.from(new Set((interests || [])
    .map((interest) => getInterestOptionId(interest))
    .filter(Boolean)));
}

function getOtherInterest(interests?: string[]): string {
  return (
    (interests || []).find((interest) => !getInterestOptionId(interest)) || ""
  );
}

function getEditableInterests(interests?: string[]): string[] {
  const knownInterests = getKnownInterestIds(interests);
  return getOtherInterest(interests)
    ? [...knownInterests, OTHER_INTEREST]
    : knownInterests;
}

function formatCreatedAt(createdAt: unknown): string {
  let date: Date | null = null;

  if (createdAt instanceof Date) {
    date = createdAt;
  } else if (typeof createdAt === "string") {
    date = new Date(createdAt);
  } else if (createdAt && typeof createdAt === "object") {
    const timestamp = createdAt as FirestoreTimestampLike;
    if (typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    } else if (typeof timestamp.seconds === "number") {
      date = new Date(timestamp.seconds * 1000);
    }
  }

  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatCountryCode(countryCode?: string): string {
  const trimmed = (countryCode || "").trim();
  if (!trimmed) return "+60";
  return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
}

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const initialSchool = normalizeSchoolInfo(user);
  const [editSections, setEditSections] = useState<Record<string, boolean>>({});
  const [localResultDetails, setLocalResultDetails] = useState(
    getUserResultDetails(user),
  );
  const [localInterests, setLocalInterests] = useState(
    getEditableInterests(user?.courseFinderInterests || user?.interests),
  );
  const [localOtherInterest, setLocalOtherInterest] = useState(
    getOtherInterest(user?.courseFinderInterests || user?.interests),
  );
  const [localPersonal, setLocalPersonal] = useState({
    name: user?.name || "",
    email: user?.email || "",
    countryCode: formatCountryCode(user?.countryCode),
    country: user?.country || "Malaysia",
    phoneNumber: user?.phoneNumber || "",
    ic: user?.ic || "",
    dob: user?.dob || "",
    gender: user?.gender || "",
    state: user?.state || "",
  });
  const [localAcademic, setLocalAcademic] = useState({
    schoolName: initialSchool.schoolName,
    schoolState: initialSchool.schoolState,
    schoolYear: initialSchool.schoolYear,
    qualification: getQualificationOption(getUserQualification(user)),
    otherQualification: getOtherQualification(getUserQualification(user)),
  });
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user || editSections.personal) return;

    setLocalPersonal({
      name: user.name || "",
      email: user.email || "",
      countryCode: formatCountryCode(user.countryCode),
      country: user.country || "Malaysia",
      phoneNumber: user.phoneNumber || "",
      ic: user.ic || "",
      dob: user.dob || "",
      gender: user.gender || "",
      state: user.state || "",
    });
  }, [user, editSections.personal]);

  useEffect(() => {
    if (!user || editSections.academic) return;

    const school = normalizeSchoolInfo(user);
    setLocalAcademic({
      schoolName: school.schoolName,
      schoolState: school.schoolState,
      schoolYear: school.schoolYear,
      qualification: getQualificationOption(getUserQualification(user)),
      otherQualification: getOtherQualification(getUserQualification(user)),
    });
    setLocalResultDetails(getUserResultDetails(user));
  }, [user, editSections.academic]);

  useEffect(() => {
    if (!user || editSections.preferences) return;

    setLocalInterests(
      getEditableInterests(user.courseFinderInterests || user.interests),
    );
    setLocalOtherInterest(
      getOtherInterest(user.courseFinderInterests || user.interests),
    );
  }, [user, editSections.preferences]);

  if (!user) return null;

  const profileCompletion = computeProfileCompletion(user);

  const setEditing = (section: string, val: boolean) => {
    if (val && section === "personal") {
      setLocalPersonal({
        name: user.name || "",
        email: user.email || "",
        countryCode: formatCountryCode(user.countryCode),
        country: user.country || "Malaysia",
        phoneNumber: user.phoneNumber || "",
        ic: user.ic || "",
        dob: user.dob || "",
        gender: user.gender || "",
        state: user.state || "",
      });
    }
    if (val && section === "academic") {
      const school = normalizeSchoolInfo(user);
      setLocalAcademic({
        schoolName: school.schoolName,
        schoolState: school.schoolState,
        schoolYear: school.schoolYear,
        qualification: getQualificationOption(getUserQualification(user)),
        otherQualification: getOtherQualification(getUserQualification(user)),
      });
      setLocalResultDetails(getUserResultDetails(user));
    }
    if (val && section === "preferences") {
      setLocalInterests(
        getEditableInterests(user.courseFinderInterests || user.interests),
      );
      setLocalOtherInterest(
        getOtherInterest(user.courseFinderInterests || user.interests),
      );
    }
    setEditSections((prev) => ({ ...prev, [section]: val }));
  };

  const handleSave = (section: string) => {
    if (section === "academic") {
      const trimmedOtherQualification = localAcademic.otherQualification.trim();
      const qualification =
        localAcademic.qualification === "Other"
          ? trimmedOtherQualification
          : localAcademic.qualification;
      const shouldSaveCgpa = shouldUseCgpaInput(localAcademic.qualification);
      const updatedGrades = shouldSaveCgpa
        ? localResultDetails
            .filter((grade) => grade.subject === "CGPA" || grade.grade.trim())
            .slice(0, 1)
            .map((grade) => ({
              subject: "CGPA",
              grade: stripCgpaSuffix(grade.grade),
            }))
        : localResultDetails;

      if (
        localAcademic.qualification === "Other" &&
        !trimmedOtherQualification
      ) {
        setErrors((prev) => ({
          ...prev,
          academicQualification: "Please enter your qualification.",
        }));
        return;
      }

      const updated = {
        school: {
          schoolName: localAcademic.schoolName,
          schoolState: localAcademic.schoolState,
          schoolYear: localAcademic.schoolYear,
        },
        resultDetails: updatedGrades,
        qualification,
      } as Partial<Student>;
      updateUser({
        ...updated,
        profileCompletion: computeProfileCompletion({ ...user, ...updated }),
      });
    }
    if (section === "preferences") {
      const trimmedOtherInterest = localOtherInterest.trim();

      if (localInterests.includes(OTHER_INTEREST) && !trimmedOtherInterest) {
        setErrors((prev) => ({
          ...prev,
          preferencesInterest: "Please enter your field of interest.",
        }));
        return;
      }

      const updated = {
        courseFinderInterests: localInterests
          .filter((interest) => interest !== OTHER_INTEREST)
          .concat(
            localInterests.includes(OTHER_INTEREST)
              ? [trimmedOtherInterest]
              : [],
          ),
      };
      updateUser({
        ...updated,
        profileCompletion: computeProfileCompletion({ ...user, ...updated }),
      });
    }
    if (section === "personal") {
      const updated = {
        name: localPersonal.name,
        countryCode: formatCountryCode(localPersonal.countryCode),
        country: localPersonal.country,
        phoneNumber: localPersonal.phoneNumber,
        ic: localPersonal.ic,
        dob: localPersonal.dob,
        gender: localPersonal.gender,
        state: localPersonal.state,
      };
      updateUser({
        ...updated,
        profileCompletion: computeProfileCompletion({ ...user, ...updated }),
      });
    }
    setErrors((prev) => ({
      ...prev,
      [section]: "",
      academicQualification: "",
      preferencesInterest: "",
    }));
    setEditing(section, false);
    setSaved((prev) => ({ ...prev, [section]: true }));
    setTimeout(() => setSaved((prev) => ({ ...prev, [section]: false })), 2000);
  };

  const tierColors: Record<string, string> = {
    Bronze: "from-amber-500 to-amber-600",
    Silver: "from-slate-400 to-slate-500",
    Gold: "from-yellow-400 to-amber-500",
    Platinum: "from-cyan-400 to-blue-500",
  };

  const toggleInterest = (interest: string) =>
    setLocalInterests((prev) => {
      const hasInterest = prev.includes(interest);
      if (interest === OTHER_INTEREST && hasInterest) {
        setLocalOtherInterest("");
      }
      if (interest === NOT_SURE_INTEREST) {
        setLocalOtherInterest("");
        return hasInterest ? [] : [interest];
      }
      const selectableInterests = prev.filter((i) => i !== NOT_SURE_INTEREST);
      return hasInterest
        ? selectableInterests.filter((i) => i !== interest)
        : [...selectableInterests, interest];
    });

  const addGrade = () =>
    setLocalResultDetails((prev) => [...prev, { subject: "", grade: "A" }]);
  const removeGrade = (i: number) =>
    setLocalResultDetails((prev) => prev.filter((_, idx) => idx !== i));
  const updateGrade = (i: number, key: "subject" | "grade", val: string) => {
    setLocalResultDetails((prev) => {
      const u = [...prev];
      u[i] = { ...u[i], [key]: val };
      return u;
    });
  };
  const getSubjectOptionValue = (subject: string) => {
    if (!subject) return "";
    return SUBJECTS.includes(subject) ? subject : OTHER_SUBJECT;
  };
  const isOtherSubject = (subject: string) =>
    subject === OTHER_SUBJECT || (!!subject && !SUBJECTS.includes(subject));
  const updateOtherSubject = (i: number, val: string) => {
    updateGrade(i, "subject", val || OTHER_SUBJECT);
  };
  const updateCgpaGrade = (val: string) => {
    setLocalResultDetails([{ subject: "CGPA", grade: stripCgpaSuffix(val) }]);
  };

  const academicQualification =
    localAcademic.qualification === "Other" &&
    localAcademic.otherQualification.trim()
      ? localAcademic.otherQualification.trim()
      : localAcademic.qualification || getUserQualification(user);
  const isForm4Qualification = academicQualification === "Form 4";
  const usesCgpaInput = shouldUseCgpaInput(localAcademic.qualification);
  const savedQualificationOption = getQualificationOption(
    getUserQualification(user),
  );
  const resultUsesCgpaInput = shouldUseCgpaInput(
    localAcademic.qualification || savedQualificationOption,
  );
  const resultDetails = getUserResultDetails(user);
  const cgpaValue = stripCgpaSuffix(
    localResultDetails.find((grade) => grade.subject === "CGPA")?.grade ||
      localResultDetails[0]?.grade ||
      "",
  );
  const examYearLabel = academicQualification
    ? `${academicQualification} Year`
    : "SPM/UEC Year";
  const examResultsLabel = academicQualification
    ? `${academicQualification} Results`
    : "SPM/UEC Results";
  const courseFinderAnswers = user.courseFinderResult?.answers;
  const courseFinderInterests =
    courseFinderAnswers?.courseFinderInterests?.length
      ? courseFinderAnswers.courseFinderInterests
      : user.courseFinderInterests?.length
        ? user.courseFinderInterests
        : user.courseFinderAnswers?.courseFinderInterests ||
          user.courseFinderAnswers?.interests ||
          [];
  const hasCourseFinderResults =
    !!user.courseFinderResult ||
    !!user.courseFinderAnswers ||
    courseFinderInterests.length > 0 ||
    !!courseFinderAnswers?.qualification ||
    !!user.qualification ||
    !!user.budget;

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1
          className="text-[28px] text-slate-900 tracking-tight"
          style={{ fontWeight: 900 }}
        >
          My Profile
        </h1>
        <p
          className="text-[14px] text-slate-500 mt-1"
          style={{ fontWeight: 400 }}
        >
          Keep your profile up-to-date to get the best university and
          scholarship matches.
        </p>
      </motion.div>

      {/* Profile hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 mb-6 flex flex-col sm:flex-row items-center gap-6"
      >
        {/* Avatar */}
        <div className="relative">
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center text-white text-[28px]"
            style={{ backgroundColor: user.avatarColor, fontWeight: 900 }}
          >
            {user.name.charAt(0)}
          </div>
          {/* <button className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-violet-600 flex items-center justify-center shadow-md hover:bg-violet-700 transition-colors">
            <Camera className="w-3.5 h-3.5 text-white" />
          </button> */}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h2
            className="text-[22px] text-slate-900 tracking-tight"
            style={{ fontWeight: 800 }}
          >
            {user.name}
          </h2>
          <p
            className="text-[13px] text-slate-500 mb-2"
            style={{ fontWeight: 400 }}
          >
            {user.email}
          </p>
          <div className="flex items-center gap-2 justify-center sm:justify-start flex-wrap">
            {/* <span
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-[11px] bg-gradient-to-r ${tierColors[user.tier]} shadow-sm`}
              style={{ fontWeight: 700 }}
            >
              ⭐ {user.tier} Member
            </span> */}
            <span
              className="text-[11px] text-slate-500 px-3 py-1 rounded-full bg-slate-50 border border-slate-200"
              style={{ fontWeight: 500 }}
            >
              Joined {formatCreatedAt(user.createdAt)}
            </span>
          </div>
        </div>
        {/* Completion */}
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full border-4 border-violet-100 flex items-center justify-center relative">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 64 64">
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="#EDE9FE"
                strokeWidth="6"
              />
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="#7C3AED"
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - profileCompletion / 100)}`}
                strokeLinecap="round"
              />
            </svg>
            <span
              className="text-[14px] text-violet-700"
              style={{ fontWeight: 800 }}
            >
              {profileCompletion}%
            </span>
          </div>
          <p
            className="text-[11px] text-slate-500 mt-1.5 text-center"
            style={{ fontWeight: 500 }}
          >
            Profile complete
          </p>
        </div>
      </motion.div>

      <div className="flex flex-col gap-5">
        {/* ── Course Finder Results ── */}
        {hasCourseFinderResults ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07 }}
          >
            <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span
                    className="text-[15px] text-white"
                    style={{ fontWeight: 700 }}
                  >
                    Course Finder Results
                  </span>
                </div>
                <NavLink
                  to="/course-finder"
                  className="flex items-center gap-1 text-[11px] text-violet-200 hover:text-white transition-colors"
                  style={{ fontWeight: 600 }}
                >
                  Retake <ChevronRight className="w-3 h-3" />
                </NavLink>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {[
                  {
                    icon: <GraduationCap className="w-3 h-3" />,
                    label: "Qualification",
                    value:
                      courseFinderAnswers?.qualification ||
                      getUserQualification(user),
                  },
                  {
                    icon: <BookOpen className="w-3 h-3" />,
                    label: "Result Range",
                    value:
                      courseFinderAnswers?.estimatedResult ||
                      user.estimatedResult,
                  },
                  {
                    icon: <MapPin className="w-3 h-3" />,
                    label: "Study Option",
                    value: courseFinderAnswers?.pathway || user.pathway,
                  },
                  {
                    icon: <DollarSign className="w-3 h-3" />,
                    label: "Budget",
                    value: courseFinderAnswers?.budget || user.budget,
                  },
                  {
                    icon: <Clock className="w-3 h-3" />,
                    label: "Timeline",
                    value: courseFinderAnswers?.timeline || user.timeline,
                  },
                ]
                  .filter((item) => item.value)
                  .map((item, i) => (
                    <div key={i} className="bg-white/10 rounded-2xl p-3">
                      <div className="flex items-center gap-1 text-violet-200 mb-1">
                        {item.icon}
                        <span
                          className="text-[10px]"
                          style={{ fontWeight: 600 }}
                        >
                          {item.label.toUpperCase()}
                        </span>
                      </div>
                      <p
                        className="text-[12px] text-white capitalize"
                        style={{ fontWeight: 600 }}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
              </div>
              {courseFinderInterests.length > 0 && (
                <div className="mt-3">
                  <p
                    className="text-[10px] text-violet-300 mb-2"
                    style={{ fontWeight: 600 }}
                  >
                    STUDY INTERESTS
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {courseFinderInterests.map((id) => (
                      <span
                        key={id}
                        className="px-2.5 py-1 rounded-full bg-white/20 text-white text-[11px]"
                        style={{ fontWeight: 600 }}
                      >
                        {id.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07 }}
          >
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl border-2 border-dashed border-violet-200 p-6 flex flex-col sm:flex-row items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p
                  className="text-[14px] text-violet-900 mb-1"
                  style={{ fontWeight: 700 }}
                >
                  Haven't done the Course Finder yet?
                </p>
                <p
                  className="text-[12px] text-violet-600"
                  style={{ fontWeight: 400 }}
                >
                  Find courses and universities that match your interests,
                  results and budget.
                </p>
              </div>
              <NavLink
                to="/course-finder"
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-[12px] hover:bg-violet-700 transition-colors"
                style={{ fontWeight: 600 }}
              >
                Start Course Finder <ChevronRight className="w-3.5 h-3.5" />
              </NavLink>
            </div>
          </motion.div>
        )}

        {/* Personal Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <SectionCard
            title="Personal Information"
            icon={<User className="w-4 h-4" />}
            editing={!!editSections.personal}
            onEdit={() => {
              setLocalPersonal({
                name: user.name || "",
                email: user.email || "",
                countryCode: formatCountryCode(user.countryCode),
                country: user.country || "Malaysia",
                phoneNumber: user.phoneNumber || "",
                ic: user.ic || "",
                dob: user.dob || "",
                gender: user.gender || "",
                state: user.state || "",
              });
              setEditing("personal", true);
            }}
            onCancel={() => setEditing("personal", false)}
            onSave={() => handleSave("personal")}
          >
            {saved.personal && (
              <p
                className="text-[12px] text-green-600 mb-3 bg-green-50 px-3 py-2 rounded-xl"
                style={{ fontWeight: 600 }}
              >
                ✓ Changes saved successfully
              </p>
            )}
            <div className="flex flex-col gap-4">
              <FieldRow
                label="Name"
                value={localPersonal.name}
                editing={!!editSections.personal}
                onChange={(v) => setLocalPersonal((p) => ({ ...p, name: v }))}
              />
              <FieldRow
                label="Email"
                value={user.email}
                editing={false}
                type="email"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <FieldRow
                  label="Country Code"
                  value={localPersonal.countryCode}
                  editing={!!editSections.personal}
                >
                  <select
                    name="countryCode"
                    id="countryCode"
                    value={localPersonal.countryCode}
                    onChange={(e) => {
                      const selected = countryData.find(
                        (c) =>
                          formatCountryCode(c.countryCode) === e.target.value,
                      );
                      setLocalPersonal((p) => ({
                        ...p,
                        countryCode: e.target.value,
                        country: selected?.countryName ?? p.country,
                      }));
                    }}
                    className="flex-1 px-3 py-2 w-full rounded-xl border border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                  >
                    {countryData.map((c) => (
                      <option
                        key={c.isoCode}
                        value={formatCountryCode(c.countryCode)}
                      >
                        {c.countryName} (+{c.countryCode})
                      </option>
                    ))}
                  </select>
                </FieldRow>
                <FieldRow
                  label="Phone"
                  value={localPersonal.phoneNumber}
                  editing={!!editSections.personal}
                  type="tel"
                  onChange={(v) =>
                    setLocalPersonal((p) => ({ ...p, phoneNumber: v }))
                  }
                />
              </div>
              <FieldRow
                label="IC Number"
                value={localPersonal.ic}
                editing={!!editSections.personal}
                onChange={(v) => setLocalPersonal((p) => ({ ...p, ic: v }))}
              />
              <FieldRow
                label="Date of Birth"
                value={localPersonal.dob}
                editing={!!editSections.personal}
                type="date"
                onChange={(v) => setLocalPersonal((p) => ({ ...p, dob: v }))}
              />
              <FieldRow
                label="Gender"
                value={localPersonal.gender || ""}
                editing={!!editSections.personal}
              >
                <select
                  value={localPersonal.gender}
                  onChange={(e) =>
                    setLocalPersonal((p) => ({ ...p, gender: e.target.value }))
                  }
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  <option value="" disabled>
                    Please select
                  </option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </FieldRow>
              <FieldRow
                label="State"
                value={localPersonal.state}
                editing={!!editSections.personal}
              >
                <select
                  value={localPersonal.state}
                  onChange={(e) =>
                    setLocalPersonal((p) => ({ ...p, state: e.target.value }))
                  }
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  <option value="" disabled>
                    Please select
                  </option>
                  {STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </FieldRow>
            </div>
          </SectionCard>
        </motion.div>

        {/* Academic */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <SectionCard
            title="Academic Background"
            icon={<span className="text-[16px]">🎓</span>}
            editing={!!editSections.academic}
            onEdit={() => {
              setEditing("academic", true);
            }}
            onCancel={() => setEditing("academic", false)}
            onSave={() => handleSave("academic")}
          >
            {saved.academic && (
              <p
                className="text-[12px] text-green-600 mb-3 bg-green-50 px-3 py-2 rounded-xl"
                style={{ fontWeight: 600 }}
              >
                ✓ Changes saved successfully
              </p>
            )}
            <div className="flex flex-col gap-4">
              <FieldRow
                label="School"
                value={localAcademic.schoolName}
                editing={!!editSections.academic}
                onChange={(v) =>
                  setLocalAcademic((p) => ({ ...p, schoolName: v }))
                }
              />
              <FieldRow
                label="Qualifications"
                value={academicQualification}
                editing={!!editSections.academic}
              >
                <select
                  value={localAcademic.qualification}
                  onChange={(e) => {
                    const nextQualification = e.target.value;
                    setLocalAcademic((p) => ({
                      ...p,
                      qualification: nextQualification,
                      otherQualification:
                        nextQualification === "Other"
                          ? p.otherQualification
                          : "",
                    }));
                    setLocalResultDetails((prev) => {
                      if (shouldUseCgpaInput(nextQualification)) {
                        return [
                          {
                            subject: "CGPA",
                            grade:
                              prev.find((grade) => grade.subject === "CGPA")
                                ?.grade || "",
                          },
                        ];
                      }

                      return prev.length === 1 && prev[0].subject === "CGPA"
                        ? []
                        : prev;
                    });
                    setErrors((prev) => ({
                      ...prev,
                      academicQualification: "",
                    }));
                  }}
                  className="flex-1 px-3 py-2 w-full rounded-xl border border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  <option value="" disabled>
                    Please select
                  </option>
                  {QUALIFICATIONS.map((qualification) => (
                    <option key={qualification.id} value={qualification.id}>
                      {qualification.label}
                    </option>
                  ))}
                </select>
              </FieldRow>
              {editSections.academic &&
                localAcademic.qualification === "Other" && (
                  <div>
                    <FieldRow
                      label="Other qualification"
                      value={localAcademic.otherQualification}
                      editing={!!editSections.academic}
                      onChange={(v) => {
                        setLocalAcademic((p) => ({
                          ...p,
                          otherQualification: v,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          academicQualification: "",
                        }));
                      }}
                    />
                    {errors.academicQualification && (
                      <p
                        className="mt-1 text-[12px] text-red-600 sm:ml-28"
                        style={{ fontWeight: 500 }}
                      >
                        {errors.academicQualification}
                      </p>
                    )}
                  </div>
                )}
              <div className="grid sm:grid-cols-2 gap-4">
                <FieldRow
                  label="State"
                  value={localAcademic.schoolState}
                  editing={!!editSections.academic}
                >
                  <select
                    value={localAcademic.schoolState}
                    onChange={(e) =>
                      setLocalAcademic((p) => ({
                        ...p,
                        schoolState: e.target.value,
                      }))
                    }
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[13px] outline-none focus:border-violet-400 transition-all"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    <option value="" disabled>
                      Please select
                    </option>
                    {STATES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </FieldRow>
                {!isForm4Qualification && (
                  <FieldRow
                    label={examYearLabel}
                    value={localAcademic.schoolYear}
                    editing={!!editSections.academic}
                    type="number"
                    onChange={(v) =>
                      setLocalAcademic((p) => ({ ...p, schoolYear: v }))
                    }
                  />
                )}
              </div>

              {!isForm4Qualification && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-[12px] text-slate-500"
                      style={{ fontWeight: 500 }}
                    >
                      {examResultsLabel}
                    </span>
                    {editSections.academic && !usesCgpaInput && (
                      <button
                        onClick={addGrade}
                        className="flex items-center gap-1 text-[11px] text-violet-600"
                        style={{ fontWeight: 600 }}
                      >
                        <Plus className="w-3 h-3" /> Add subject
                      </button>
                    )}
                  </div>
                  {editSections.academic && usesCgpaInput ? (
                    <FieldRow
                      label="CGPA"
                      value={cgpaValue}
                      editing={!!editSections.academic}
                      type="number"
                      onChange={updateCgpaGrade}
                    />
                  ) : editSections.academic ? (
                    <div className="flex flex-col gap-2">
                      {localResultDetails.map((g, i) => (
                        <div
                          key={i}
                          className="flex flex-col gap-2 sm:flex-row"
                        >
                          <select
                            value={getSubjectOptionValue(g.subject)}
                            onChange={(e) =>
                              updateGrade(i, "subject", e.target.value)
                            }
                            className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[12px] outline-none focus:border-violet-300 transition-all"
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 400,
                            }}
                          >
                            <option value="" disabled>
                              Please select
                            </option>
                            {SUBJECTS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                          {isOtherSubject(g.subject) && (
                            <input
                              value={
                                g.subject === OTHER_SUBJECT ? "" : g.subject
                              }
                              onChange={(e) =>
                                updateOtherSubject(i, e.target.value)
                              }
                              placeholder="Enter subject"
                              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[12px] outline-none focus:border-violet-300 transition-all"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                              }}
                            />
                          )}
                          <select
                            value={g.grade}
                            onChange={(e) =>
                              updateGrade(i, "grade", e.target.value)
                            }
                            className="w-full px-2 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[12px] outline-none focus:border-violet-300 transition-all sm:w-20"
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 600,
                            }}
                          >
                            {GRADES.map((gr) => (
                              <option key={gr}>{gr}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => removeGrade(i)}
                            className="self-start text-slate-400 hover:text-red-400 transition-colors sm:self-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : resultUsesCgpaInput ? (
                    <div className="flex flex-wrap gap-2">
                      {(resultDetails.length > 0
                        ? resultDetails.slice(0, 1)
                        : []
                      ).map((g, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-100"
                        >
                          <span
                            className="text-[12px] text-slate-600"
                            style={{ fontWeight: 400 }}
                          >
                            CGPA
                          </span>
                          <span
                            className="text-[12px] px-1.5 py-0.5 rounded-lg bg-green-100 text-green-700"
                            style={{ fontWeight: 700 }}
                          >
                            {stripCgpaSuffix(g.grade)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {resultDetails.map((g, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-100"
                        >
                          <span
                            className="text-[12px] text-slate-600"
                            style={{ fontWeight: 400 }}
                          >
                            {g.subject}
                          </span>
                          <span
                            className={`text-[12px] px-1.5 py-0.5 rounded-lg ${g.grade.startsWith("A") ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                            style={{ fontWeight: 700 }}
                          >
                            {g.grade}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </SectionCard>
        </motion.div>

        {/* Study Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <SectionCard
            title="Study Preferences"
            icon={<span className="text-[16px]">🎯</span>}
            editing={!!editSections.preferences}
            onEdit={() => {
              setEditing("preferences", true);
            }}
            onCancel={() => setEditing("preferences", false)}
            onSave={() => handleSave("preferences")}
          >
            {saved.preferences && (
              <p
                className="text-[12px] text-green-600 mb-3 bg-green-50 px-3 py-2 rounded-xl"
                style={{ fontWeight: 600 }}
              >
                ✓ Changes saved successfully
              </p>
            )}
            <div className="flex flex-col gap-5">
              <div>
                <span
                  className="text-[12px] text-slate-500 block mb-2"
                  style={{ fontWeight: 500 }}
                >
                  Study Pathway
                </span>
                {editSections.preferences ? (
                  <div className="grid grid-cols-3 gap-2">
                    {(["Local", "Overseas", "Both"] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => updateUser({ pathway: p })}
                        className={`py-2.5 rounded-xl border-2 text-[12px] capitalize transition-all ${user.pathway === p ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600"}`}
                        style={{ fontWeight: 600 }}
                      >
                        {p === "Local"
                          ? "🇲🇾 Local"
                          : p === "Overseas"
                            ? "🌍 Overseas"
                            : "🌐 Both"}
                      </button>
                    ))}
                  </div>
                ) : (
                  <span
                    className="text-[14px] text-slate-800 capitalize"
                    style={{ fontWeight: 500 }}
                  >
                    {formatStudyPathway(user.pathway)}
                  </span>
                )}
              </div>

              <div>
                <span
                  className="text-[12px] text-slate-500 block mb-2"
                  style={{ fontWeight: 500 }}
                >
                  Fields of Interest
                </span>
                {editSections.preferences ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                      {INTERESTS.map((interest) => (
                        <button
                          key={interest.id}
                          type="button"
                          onClick={() => toggleInterest(interest.id)}
                          className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${
                            localInterests.includes(interest.id)
                              ? "bg-violet-50 border-violet-300 text-violet-700"
                              : "bg-white border-slate-200 text-slate-600"
                          }`}
                          style={{
                            fontWeight: localInterests.includes(interest.id)
                              ? 600
                              : 400,
                          }}
                        >
                          {interest.label}
                        </button>
                      ))}
                    </div>
                    {localInterests.includes(OTHER_INTEREST) && (
                      <div>
                        <input
                          value={localOtherInterest}
                          onChange={(e) => {
                            setLocalOtherInterest(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              preferencesInterest: "",
                            }));
                          }}
                          placeholder="Enter your field of interest"
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-800 text-[12px] outline-none focus:border-violet-300 transition-all"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 400,
                          }}
                        />
                        {errors.preferencesInterest && (
                          <p
                            className="mt-1 text-[11px] text-red-500"
                            style={{ fontWeight: 500 }}
                          >
                            {errors.preferencesInterest}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(user.courseFinderInterests || user.interests).map((i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100 text-[12px] text-violet-700"
                        style={{ fontWeight: 600 }}
                      >
                        {i}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <span
                  className="text-[12px] text-slate-500 block mb-2"
                  style={{ fontWeight: 500 }}
                >
                  Annual Budget
                </span>
                {editSections.preferences ? (
                  <div className="flex flex-col gap-2">
                    {BUDGETS.map((b) => {
                      const selectedBudget = getBudgetOption(user.budget);
                      const isSelected = selectedBudget?.id === b.id;

                      return (
                        <button
                          key={b.id}
                          onClick={() => updateUser({ budget: b.id })}
                          className={`px-4 py-2.5 rounded-xl border-2 text-[12px] text-left transition-all ${isSelected ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600"}`}
                          style={{ fontWeight: isSelected ? 600 : 400 }}
                        >
                          {b.label}
                          {b.sub && (
                            <span className="ml-1 text-slate-400">{b.sub}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <span
                    className="text-[14px] text-slate-800"
                    style={{ fontWeight: 500 }}
                  >
                    {getBudgetOption(user.budget)?.label || user.budget || "—"}
                  </span>
                )}
              </div>
            </div>
          </SectionCard>
        </motion.div>

        {/* Notifications */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
                <Bell className="w-4 h-4 text-violet-600" />
              </div>
              <span
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Notification Preferences
              </span>
            </div>
            <div className="flex flex-col gap-4">
              {[
                {
                  label: "Application updates",
                  sub: "Status changes, offer letters",
                  on: true,
                },
                {
                  label: "Scholarship alerts",
                  sub: "New matches, deadlines",
                  on: true,
                },
                {
                  label: "Counselor messages",
                  sub: "Chat notifications",
                  on: true,
                },
                {
                  label: "Deadline reminders",
                  sub: "7 days, 3 days, 1 day before",
                  on: false,
                },
                {
                  label: "Promotional content",
                  sub: "Tips, news & special offers",
                  on: false,
                },
              ].map((notif, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div>
                    <p
                      className="text-[13px] text-slate-800"
                      style={{ fontWeight: 600 }}
                    >
                      {notif.label}
                    </p>
                    <p
                      className="text-[11px] text-slate-400"
                      style={{ fontWeight: 400 }}
                    >
                      {notif.sub}
                    </p>
                  </div>
                  <div
                    className={`w-11 h-6 rounded-full transition-colors cursor-pointer ${notif.on ? "bg-violet-600" : "bg-slate-200"}`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-sm mt-1 transition-transform ${notif.on ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div> */}

        {/* Danger zone */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white rounded-3xl border border-red-100 shadow-sm p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center">
                <Shield className="w-4 h-4 text-red-500" />
              </div>
              <span
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Account
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-[13px] text-slate-600 hover:bg-slate-50 transition-colors"
                style={{ fontWeight: 500 }}
              >
                Change Password
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 text-[13px] text-red-500 hover:bg-red-50 transition-colors"
                style={{ fontWeight: 500 }}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete Account
              </button>
            </div>
          </div>
        </motion.div> */}
      </div>
    </div>
  );
}
